import { useEffect, useRef } from "react";
import { useTheme } from "../contexts/ThemeContext";

/**
 * Looping multi-scene hero animation in vanilla canvas 2D.
 * Reacts to theme: dark = saturated colors on black. light = darker colors on cream.
 */
export default function HeroCanvas() {
  const canvasRef = useRef(null);
  const { theme } = useTheme();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let rafId;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let W = 0;
    let H = 0;

    const dark = theme === "dark";
    const colors = dark
      ? {
          bg: "#050505",
          green: "#2a912b",
          darkGreen: "#1c5b1d",
          yellow: "#fce74d",
          lime: "#a8d80b",
          teal: "#34c47e",
          gridLine: "rgba(255,255,255,0.025)",
          scanGrad: ["rgba(252,231,77,0)", "rgba(252,231,77,0.18)", "rgba(252,231,77,0)"],
          tunnelInner: "rgba(252,231,77,0.35)",
          vignette: "rgba(42,145,43,0.08)",
          tealGlow: "rgba(52,196,126,0.18)",
          serverInner: "rgba(42,145,43,0.35)",
          scanlineGrad: ["rgba(52,196,126,0)", "rgba(52,196,126,0.25)", "rgba(52,196,126,0)"],
          dotShadow: 14,
        }
      : {
          bg: "#ffffff",
          green: "#1f6b22",
          darkGreen: "#0f4513",
          yellow: "#c9ad00",
          lime: "#4d7503",
          teal: "#0f7d96",
          gridLine: "rgba(69,188,216,0.14)",
          scanGrad: ["rgba(201,173,0,0)", "rgba(201,173,0,0.32)", "rgba(201,173,0,0)"],
          tunnelInner: "rgba(30,165,194,0.22)",
          vignette: "rgba(69,188,216,0.08)",
          tealGlow: "rgba(15,125,150,0.32)",
          serverInner: "rgba(31,107,34,0.45)",
          scanlineGrad: ["rgba(15,125,150,0)", "rgba(15,125,150,0.34)", "rgba(15,125,150,0)"],
          dotShadow: 6,
        };

    function resize() {
      const rect = canvas.getBoundingClientRect();
      W = rect.width;
      H = rect.height;
      canvas.width = Math.floor(W * dpr);
      canvas.height = Math.floor(H * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    resize();
    window.addEventListener("resize", resize);

    // ---------- Scene 1: Pixelated map ----------
    const mapPoints = [];
    const seedClusters = [
      [0.18, 0.5, 0.1, 60],
      [0.28, 0.65, 0.07, 35],
      [0.48, 0.4, 0.12, 80],
      [0.55, 0.6, 0.09, 50],
      [0.7, 0.45, 0.15, 100],
      [0.82, 0.7, 0.05, 25],
    ];
    seedClusters.forEach(([cx, cy, r, n], idx) => {
      for (let i = 0; i < n; i++) {
        const a = Math.random() * Math.PI * 2;
        const rr = Math.random() ** 1.4 * r;
        mapPoints.push({
          x: cx + Math.cos(a) * rr,
          y: cy + Math.sin(a) * rr * 0.6,
          base: Math.random() * 0.6 + 0.2,
          highlight: idx === 5 && Math.random() < 0.4,
          phase: Math.random() * Math.PI * 2,
        });
      }
    });

    const meshNodes = [];
    const meshCount = 60;
    for (let i = 0; i < meshCount; i++) {
      meshNodes.push({
        x: Math.random(),
        y: Math.random(),
        z: Math.random(),
        vx: (Math.random() - 0.5) * 0.0006,
        vy: (Math.random() - 0.5) * 0.0006,
      });
    }

    const tunnelRings = 18;

    const bars = [];
    for (let i = 0; i < 24; i++) {
      bars.push({
        h: Math.random(),
        target: Math.random(),
        speed: Math.random() * 0.4 + 0.2,
      });
    }

    // Scene 5: horizontal line-burst — stripes converging to a central vertical
    const stripeRows = 28;

    const SCENES = 5;
    const SCENE_DURATION = 7000;
    const FADE = 1100;
    const start = performance.now();

    function drawScene1(t, alpha) {
      ctx.save();
      ctx.globalAlpha = alpha;
      const cellSize = Math.max(3, Math.min(W, H) / 180);
      mapPoints.forEach((p) => {
        const px = p.x * W;
        const py = p.y * H;
        const pulse = 0.55 + Math.sin(t * 0.0015 + p.phase) * 0.45;
        const size = cellSize * (p.base + pulse * 0.4);
        if (p.highlight) {
          ctx.fillStyle = colors.yellow;
          ctx.shadowColor = colors.yellow;
          ctx.shadowBlur = colors.dotShadow;
        } else {
          const v = p.base * pulse;
          ctx.fillStyle =
            v > 0.55 ? colors.lime : v > 0.35 ? colors.green : colors.darkGreen;
          ctx.shadowBlur = 0;
        }
        ctx.fillRect(px - size / 2, py - size / 2, size, size);
      });
      ctx.shadowBlur = 0;

      const scanX = ((t * 0.05) % (W + 200)) - 100;
      const grad = ctx.createLinearGradient(scanX - 60, 0, scanX + 60, 0);
      grad.addColorStop(0, colors.scanGrad[0]);
      grad.addColorStop(0.5, colors.scanGrad[1]);
      grad.addColorStop(1, colors.scanGrad[2]);
      ctx.fillStyle = grad;
      ctx.fillRect(scanX - 60, 0, 120, H);
      ctx.restore();
    }

    function drawScene2(t, alpha) {
      ctx.save();
      ctx.globalAlpha = alpha;
      meshNodes.forEach((n) => {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > 1) n.vx *= -1;
        if (n.y < 0 || n.y > 1) n.vy *= -1;
      });

      const ang = t * 0.0003;
      const cos = Math.cos(ang);
      const sin = Math.sin(ang);
      const projected = meshNodes.map((n) => {
        const x3 = (n.x - 0.5) * 1.2;
        const z3 = (n.z - 0.5) * 1.2;
        const y3 = (n.y - 0.5) * 1.2;
        const xr = x3 * cos - z3 * sin;
        const zr = x3 * sin + z3 * cos;
        const persp = 1.6 / (1.8 + zr);
        return {
          px: W / 2 + xr * persp * W * 0.55,
          py: H / 2 + y3 * persp * H * 0.55,
          d: zr,
        };
      });

      ctx.lineWidth = 0.7;
      const maxDist = Math.min(W, H) * 0.18;
      const teal = dark ? "52,196,126" : "15,125,150";
      for (let i = 0; i < projected.length; i++) {
        for (let j = i + 1; j < projected.length; j++) {
          const a = projected[i];
          const b = projected[j];
          const dx = a.px - b.px;
          const dy = a.py - b.py;
          const d = Math.hypot(dx, dy);
          if (d < maxDist) {
            const op = (1 - d / maxDist) * (dark ? 0.55 : 0.45);
            ctx.strokeStyle = `rgba(${teal},${op})`;
            ctx.beginPath();
            ctx.moveTo(a.px, a.py);
            ctx.lineTo(b.px, b.py);
            ctx.stroke();
          }
        }
      }

      projected.forEach((p, i) => {
        ctx.fillStyle = i % 7 === 0 ? colors.yellow : colors.lime;
        ctx.shadowColor = i % 7 === 0 ? colors.yellow : colors.lime;
        ctx.shadowBlur = i % 7 === 0 ? (dark ? 12 : 6) : (dark ? 4 : 2);
        ctx.beginPath();
        ctx.arc(p.px, p.py, i % 7 === 0 ? 2.6 : 1.6, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.shadowBlur = 0;
      ctx.restore();
    }

    function drawScene3(t, alpha) {
      ctx.save();
      ctx.globalAlpha = alpha;
      const cx = W / 2;
      const cy = H / 2;
      ctx.lineWidth = 1;

      const speed = (t * 0.0005) % 1;
      const limeRGB = dark ? "168,216,11" : "63,87,4";
      for (let i = 0; i < tunnelRings; i++) {
        const z = ((i + speed) % tunnelRings) / tunnelRings;
        const scale = Math.pow(z, 1.3);
        const w = W * 0.95 * scale;
        const h = H * 0.95 * scale;
        const op = Math.min(1, scale * 1.4) * (1 - Math.pow(1 - scale, 4));
        ctx.strokeStyle = `rgba(${limeRGB},${op * (dark ? 0.7 : 0.55)})`;
        ctx.strokeRect(cx - w / 2, cy - h / 2, w, h);
      }

      const spokes = 24;
      ctx.strokeStyle = colors.tealGlow;
      for (let i = 0; i < spokes; i++) {
        const a = (i / spokes) * Math.PI * 2;
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.lineTo(cx + Math.cos(a) * W, cy + Math.sin(a) * H);
        ctx.stroke();
      }

      const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, 120);
      grad.addColorStop(0, colors.tunnelInner);
      grad.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, H);
      ctx.restore();
    }

    function drawScene4(t, alpha) {
      ctx.save();
      ctx.globalAlpha = alpha;
      const baseY = H * 0.78;
      const topY = H * 0.18;
      const totalH = baseY - topY;
      const barW = W / bars.length;

      ctx.strokeStyle = colors.lime + (dark ? "" : "");
      ctx.globalAlpha = alpha * (dark ? 0.5 : 0.6);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, baseY);
      ctx.lineTo(W, baseY);
      ctx.stroke();
      ctx.globalAlpha = alpha;

      bars.forEach((b, i) => {
        if (Math.random() < 0.02) b.target = Math.random();
        b.h += (b.target - b.h) * 0.02 * b.speed;
        const h = totalH * (0.25 + b.h * 0.7);
        const x = i * barW + 6;
        const w = barW - 12;
        const y = baseY - h;

        ctx.strokeStyle = colors.green;
        ctx.lineWidth = 1;
        ctx.strokeRect(x, y, w, h);

        const rows = Math.max(3, Math.floor(h / 14));
        for (let r = 0; r < rows; r++) {
          const ry = y + (r / rows) * h;
          ctx.strokeStyle = colors.serverInner;
          ctx.beginPath();
          ctx.moveTo(x, ry);
          ctx.lineTo(x + w, ry);
          ctx.stroke();
          if (Math.random() < 0.04) {
            ctx.fillStyle = colors.yellow;
            ctx.fillRect(x + w - 4, ry + 2, 2, 2);
          }
        }
      });

      const sy = ((t * 0.08) % (totalH * 1.4)) + topY - totalH * 0.2;
      const grad2 = ctx.createLinearGradient(0, sy - 30, 0, sy + 30);
      grad2.addColorStop(0, colors.scanlineGrad[0]);
      grad2.addColorStop(0.5, colors.scanlineGrad[1]);
      grad2.addColorStop(1, colors.scanlineGrad[2]);
      ctx.fillStyle = grad2;
      ctx.fillRect(0, sy - 30, W, 60);

      ctx.restore();
    }

    function drawBackdrop() {
      ctx.fillStyle = colors.bg;
      ctx.fillRect(0, 0, W, H);

      const g = ctx.createRadialGradient(
        W * 0.2,
        H * 0.3,
        0,
        W * 0.5,
        H * 0.5,
        Math.max(W, H),
      );
      g.addColorStop(0, colors.vignette);
      g.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, W, H);

      ctx.strokeStyle = colors.gridLine;
      ctx.lineWidth = 1;
      const step = 60;
      for (let x = 0; x < W; x += step) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, H);
        ctx.stroke();
      }
      for (let y = 0; y < H; y += step) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(W, y);
        ctx.stroke();
      }
    }

    function drawScene5(t, alpha) {
      // Horizontal line burst — stripes flowing from edges into a central vertical column
      ctx.save();
      ctx.globalAlpha = alpha;
      const cx = W / 2;
      const rowH = H / stripeRows;
      const colW = Math.min(W * 0.18, 320); // central reveal column width
      const flow = (t * 0.15) % 60;

      // base solid bg subtle wash for depth
      const bgWash = dark
        ? "rgba(5,30,15,0.0)"
        : "rgba(255,255,255,0.0)";
      ctx.fillStyle = bgWash;
      ctx.fillRect(0, 0, W, H);

      for (let i = 0; i < stripeRows; i++) {
        const y = i * rowH + rowH / 2;
        // alternating row direction & speed
        const dir = i % 2 === 0 ? 1 : -1;
        const phase = (i * 0.7) + (t * 0.0008) * dir;
        const offset = (Math.sin(phase) * 0.5 + 0.5) * 30;
        const dashLen = 8 + ((i * 13) % 26);
        const gap = 6 + ((i * 7) % 14);

        // colors alternate teal / lime / yellow accent
        const palette = dark
          ? [colors.green, colors.teal, colors.lime]
          : [colors.green, colors.teal, colors.lime];
        const col = palette[i % 3];
        const accent = i % 9 === 0 ? colors.yellow : col;

        ctx.strokeStyle = accent;
        ctx.lineWidth = i % 9 === 0 ? 1.6 : 0.8;
        ctx.globalAlpha = alpha * (0.25 + ((i * 0.6) % 1) * 0.5);

        // LEFT side stripes flowing toward center
        ctx.setLineDash([dashLen, gap]);
        ctx.lineDashOffset = -flow * dir + offset;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(cx - colW / 2, y);
        ctx.stroke();

        // RIGHT side stripes flowing toward center
        ctx.lineDashOffset = flow * dir - offset;
        ctx.beginPath();
        ctx.moveTo(cx + colW / 2, y);
        ctx.lineTo(W, y);
        ctx.stroke();
      }
      ctx.setLineDash([]);
      ctx.globalAlpha = alpha;

      // Central glowing vertical column edges
      const colX1 = cx - colW / 2;
      const colX2 = cx + colW / 2;
      const grad = ctx.createLinearGradient(colX1 - 40, 0, colX2 + 40, 0);
      grad.addColorStop(0, "rgba(0,0,0,0)");
      grad.addColorStop(0.5, dark ? "rgba(252,231,77,0.06)" : "rgba(30,165,194,0.08)");
      grad.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = grad;
      ctx.fillRect(colX1 - 40, 0, colW + 80, H);

      // Vertical accent rules
      ctx.strokeStyle = dark ? colors.yellow : colors.teal;
      ctx.lineWidth = 1;
      ctx.globalAlpha = alpha * 0.5;
      ctx.beginPath();
      ctx.moveTo(colX1, 0);
      ctx.lineTo(colX1, H);
      ctx.moveTo(colX2, 0);
      ctx.lineTo(colX2, H);
      ctx.stroke();

      // Pulse markers along the central rules
      const markerCount = 8;
      ctx.globalAlpha = alpha;
      for (let i = 0; i < markerCount; i++) {
        const my = ((t * 0.05 + (i / markerCount) * H) % H);
        const a = 0.3 + Math.sin(t * 0.003 + i) * 0.4;
        ctx.fillStyle = dark ? colors.yellow : colors.teal;
        ctx.globalAlpha = alpha * Math.max(0, a);
        ctx.fillRect(colX1 - 2, my, 4, 4);
        ctx.fillRect(colX2 - 2, my, 4, 4);
      }
      ctx.globalAlpha = alpha;
      ctx.restore();
    }

    function frame(now) {
      const t = now - start;
      drawBackdrop();

      const cycle = SCENE_DURATION;
      const totalT = t % (cycle * SCENES);
      const idx = Math.floor(totalT / cycle);
      const localT = totalT % cycle;

      let curAlpha = 1;
      let nextAlpha = 0;
      if (localT < FADE) {
        curAlpha = localT / FADE;
        nextAlpha = 1 - curAlpha;
      }

      const prevIdx = (idx - 1 + SCENES) % SCENES;
      drawByIndex(prevIdx, t, nextAlpha);
      drawByIndex(idx, t, curAlpha);

      rafId = requestAnimationFrame(frame);
    }

    function drawByIndex(i, t, a) {
      if (a <= 0.001) return;
      if (i === 0) drawScene1(t, a);
      else if (i === 1) drawScene2(t, a);
      else if (i === 2) drawScene3(t, a);
      else if (i === 3) drawScene4(t, a);
      else drawScene5(t, a);
    }

    rafId = requestAnimationFrame(frame);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", resize);
    };
  }, [theme]);

  return (
    <canvas
      ref={canvasRef}
      className="hero-canvas"
      data-testid="hero-canvas-animation"
      aria-hidden="true"
    />
  );
}
